/**
 * The one component that writes filter parameters, so the grammar has exactly one client
 * implementation. Everything it emits is a repeated key, never a comma list, because the
 * server parses repeats and rejects anything it does not know.
 *
 * Two existing primitives do not stretch to this and are extended alongside rather than
 * forked: `Segmented` is single-select with no clear state, and `Chip` is a display span
 * with no `onClick`. `MultiSelect` and `FilterChip` live here; `Segmented` keeps its
 * current callers unchanged.
 */
import { useState, type ReactNode } from "react";
import { SEARCH_INPUT_ID } from "../lib/useListKeys";
import { useListQuery } from "../lib/useListQuery";
import type { SavedView } from "../lib/types";
import { Button } from "./ui";

export type Facet =
  | { kind: "search"; key: "q"; placeholder: string }
  | { kind: "single"; key: string; options: readonly string[]; label: string }
  | {
      kind: "multi";
      key: string;
      options: readonly { value: string; label: string; color?: string }[];
      label: string;
    }
  | { kind: "toggle"; key: string; label: string }
  | { kind: "sort"; keys: readonly string[] };

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <button
      type="button"
      aria-label={`remove ${label}`}
      onClick={onRemove}
      className="inline-flex items-center gap-1.5 bg-gold-wash px-2 py-0.5 font-mono text-[10.5px] font-medium tracking-[0.12em] text-gold uppercase hover:bg-hair-soft"
    >
      {label}
      <span aria-hidden="true">×</span>
    </button>
  );
}

function MultiSelect({
  label,
  options,
  active,
  onToggle,
}: {
  label: string;
  options: readonly { value: string; label: string; color?: string }[];
  active: readonly string[];
  onToggle: (value: string) => void;
}) {
  return (
    <fieldset className="flex items-center gap-1 border border-hair px-2 py-1">
      <legend className="sr-only">{label}</legend>
      <span className="eyebrow pe-1">{label}</span>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={active.includes(option.value)}
          onClick={() => onToggle(option.value)}
          className={`px-2 py-0.5 font-mono text-[10.5px] tracking-[0.12em] uppercase transition-colors duration-200 ease-alt ${
            active.includes(option.value)
              ? "bg-gold-surface font-bold text-on-gold"
              : "text-dim hover:text-gold"
          }`}
        >
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}

export function FilterBar({
  facets,
  views,
}: {
  facets: readonly Facet[];
  /** Saved views for this screen; omitted where the screen has none. */
  views?: { list: SavedView[]; onSave: (name: string) => void; onDelete: (id: string) => void };
}) {
  const query = useListQuery();
  const [draft, setDraft] = useState(query.get("q") ?? "");
  const [saving, setSaving] = useState("");
  const [expanded, setExpanded] = useState(false);

  const chips: ReactNode[] = [];
  for (const facet of facets) {
    // q is the facet a person is most likely to forget is set — it is why the empty
    // state says "nothing matches these filters" rather than "nothing here yet" — so it
    // gets a removable chip exactly like every other active filter.
    if (facet.kind === "search" && query.get(facet.key)) {
      chips.push(
        <FilterChip
          key={facet.key}
          label={`${facet.key} ${query.get(facet.key)}`}
          onRemove={() => {
            query.set({ [facet.key]: null });
            setDraft("");
          }}
        />,
      );
    }
    if (facet.kind === "multi" || facet.kind === "single") {
      for (const value of query.all(facet.key)) {
        chips.push(
          <FilterChip
            key={`${facet.key}:${value}`}
            label={`${facet.key} ${value}`}
            onRemove={() =>
              query.set({
                [facet.key]: query.all(facet.key).filter((one) => one !== value),
              })
            }
          />,
        );
      }
    }
    if (facet.kind === "toggle" && query.get(facet.key) === "true") {
      chips.push(
        <FilterChip
          key={facet.key}
          label={facet.label}
          onRemove={() => query.set({ [facet.key]: null })}
        />,
      );
    }
  }

  return (
    <div className="border-b border-hair">
      {/* A phone has no room for a row of fieldsets: below `lg` the bar collapses to
          this summary toggle, and the facet group itself only ever gets `hidden lg:flex`
          — never unmounted, so state inside it (the search draft, the save-view input)
          survives a collapse/expand. */}
      <button
        type="button"
        onClick={() => setExpanded((was) => !was)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between px-3 py-2 font-mono text-[11px] tracking-[0.14em] text-dim uppercase lg:hidden"
      >
        <span>
          filters{chips.length > 0 ? <span className="tnum text-gold"> · {chips.length}</span> : null}
        </span>
        <span aria-hidden="true">{expanded ? "▲" : "▼"}</span>
      </button>
      <div
        className={`${expanded ? "flex" : "hidden"} flex-wrap items-center gap-2 px-3 py-2 lg:flex`}
      >
      {facets.map((facet) => {
        switch (facet.kind) {
          case "search":
            return (
              <form
                key="q"
                onSubmit={(event) => {
                  event.preventDefault();
                  query.set({ q: draft.trim() === "" ? null : draft.trim() });
                }}
              >
                <input
                  id={SEARCH_INPUT_ID}
                  type="search"
                  aria-label={facet.placeholder}
                  placeholder={facet.placeholder}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  className="w-48 border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px] focus:border-gold focus:outline-none"
                />
              </form>
            );
          case "multi":
            return (
              <MultiSelect
                key={facet.key}
                label={facet.label}
                options={facet.options}
                active={query.all(facet.key)}
                onToggle={(value) => {
                  const active = query.all(facet.key);
                  query.set({
                    [facet.key]: active.includes(value)
                      ? active.filter((one) => one !== value)
                      : [...active, value],
                  });
                }}
              />
            );
          case "single":
            return (
              <MultiSelect
                key={facet.key}
                label={facet.label}
                options={facet.options.map((value) => ({ value, label: value }))}
                active={query.all(facet.key)}
                onToggle={(value) =>
                  query.set({ [facet.key]: query.get(facet.key) === value ? null : value })
                }
              />
            );
          case "toggle":
            return (
              <button
                key={facet.key}
                type="button"
                aria-pressed={query.get(facet.key) === "true"}
                onClick={() =>
                  query.set({ [facet.key]: query.get(facet.key) === "true" ? null : "true" })
                }
                className={`border border-hair px-2.5 py-1 font-mono text-[10.5px] tracking-[0.12em] uppercase ${
                  query.get(facet.key) === "true"
                    ? "bg-gold-surface font-bold text-on-gold"
                    : "text-dim hover:text-gold"
                }`}
              >
                {facet.label}
              </button>
            );
          case "sort":
            return (
              <label key="sort" className="eyebrow flex items-center gap-1.5">
                sort
                <select
                  aria-label="sort"
                  value={query.get("sort") ?? facet.keys[0]}
                  onChange={(event) => query.set({ sort: event.target.value })}
                  className="border border-hair bg-transparent px-2 py-1 font-mono text-[11px]"
                >
                  {facet.keys.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
              </label>
            );
        }
      })}

      {chips.length > 0 && (
        <>
          <span className="mx-1 h-4 w-px bg-hair" aria-hidden="true" />
          {chips}
          <Button variant="ghost" onClick={() => query.clear()}>
            clear
          </Button>
        </>
      )}

      {views && (
        <div className="ms-auto flex items-center gap-2">
          {views.list.map((view) => (
            <span key={view.id} className="flex items-center gap-1">
              <Button
                variant="ghost"
                onClick={() =>
                  // One atomic replace, not clear() then set(): both read the same
                  // `params` from this render's closure, and React keeps only the last
                  // setParams call — so a clear-then-set here would silently merge the
                  // view's params onto whatever was already in the bar instead of
                  // replacing it, which is the exact bug applying a view must not have.
                  query.replace(new URLSearchParams(view.query))
                }
              >
                {view.name}
              </Button>
              <button
                type="button"
                aria-label={`delete view ${view.name}`}
                onClick={() => views.onDelete(view.id)}
                className="text-faint hover:text-crit"
              >
                ×
              </button>
            </span>
          ))}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (saving.trim() === "") return;
              views.onSave(saving.trim());
              setSaving("");
            }}
          >
            <input
              aria-label="save this view as"
              placeholder="save view"
              value={saving}
              onChange={(event) => setSaving(event.target.value)}
              className="w-28 border border-hair bg-transparent px-2 py-1 font-mono text-[11px]"
            />
          </form>
        </div>
      )}
      </div>
    </div>
  );
}
